/**
 * Session participant fetch wrappers · P5.9
 *
 * Durable group-chat agent roster for a session.
 */

function trim(url: string): string {
  return url.replace(/\/$/, '');
}

export interface SessionParticipantRow {
  agent_id: string;
  agent_kind: 'claude_code' | 'codex' | string;
  display_name: string;
  harness_id: string | null;
  joined_at: number;
  joined_by_user_id: string | null;
}

export interface SessionParticipantsResult {
  session_id: string;
  participants: SessionParticipantRow[];
  fallback_all_agents: boolean;
}

export async function fetchSessionParticipants(
  backendUrl: string,
  token: string,
  sessionId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SessionParticipantsResult> {
  const resp = await fetchImpl(`${trim(backendUrl)}/api/sessions/${encodeURIComponent(sessionId)}/participants`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`fetchSessionParticipants · HTTP ${resp.status} · ${(await resp.text()).slice(0, 200)}`);
  }
  return resp.json() as Promise<SessionParticipantsResult>;
}

export async function joinSessionParticipant(
  backendUrl: string,
  token: string,
  sessionId: string,
  agentId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; participant: SessionParticipantRow; audit_id: number | null }> {
  const resp = await fetchImpl(
    `${trim(backendUrl)}/api/sessions/${encodeURIComponent(sessionId)}/participants/${encodeURIComponent(agentId)}`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: string };
    throw new Error(`joinSessionParticipant · HTTP ${resp.status} · ${err.error ?? '(no detail)'}`);
  }
  return resp.json() as Promise<{ ok: boolean; participant: SessionParticipantRow; audit_id: number | null }>;
}

export async function leaveSessionParticipant(
  backendUrl: string,
  token: string,
  sessionId: string,
  agentId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; session_id: string; agent_id: string; joined: false; audit_id?: number | null }> {
  const resp = await fetchImpl(
    `${trim(backendUrl)}/api/sessions/${encodeURIComponent(sessionId)}/participants/${encodeURIComponent(agentId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: string };
    throw new Error(`leaveSessionParticipant · HTTP ${resp.status} · ${err.error ?? '(no detail)'}`);
  }
  return resp.json() as Promise<{ ok: boolean; session_id: string; agent_id: string; joined: false; audit_id?: number | null }>;
}
